import { computed, inject } from '@angular/core';
import { EMPTY, mergeMap, pipe } from 'rxjs';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { CallStatus } from '@ngrx-traits/signals';

import { withLogger } from '@core/store';
import { DataUtil, FileUtil } from '@core/utils';
import { AssetType, FileAssetService, FileExtension, ImageFileExtension } from '@core/entities/asset';

export type Asset = {
  id: string;
  status: CallStatus;
  url: string | undefined;
  extension: FileExtension | undefined;
}

type AssetUploadState = {
  assetType: AssetType | undefined;
  accept: any;
};

export const AssetUploadStore = signalStore(
  withState<AssetUploadState>({ assetType: undefined, accept: `.${ImageFileExtension.JPG}` }),
  withEntities<Asset>(),
  withComputed(({ accept }) => ({
    supportedExtensions: computed(() => accept().split(',').map((ext: string) => ext.replace('.', ''))),
  })),
  withMethods((store, assetService = inject(FileAssetService)) => {

    const setAcceptType = (accept: string | string[]) => {
      if (!accept) return;

      patchState(store, { accept: Array.isArray(accept) ? accept.map(type => `.${type}`).join(',') : accept });
    }

    const setAssetType = (assetType: AssetType) => patchState(store, { assetType });

    const removeAsset = (id: string): void => patchState(store, removeEntity(id));

    const initAssets = (urls: string[]) => {

      patchState(
        store,
        setAllEntities(
          urls
            .filter(Boolean)
            .map(url => ({
              id: DataUtil.generateId(),
              status: 'loaded',
              url,
              extension: url ? FileUtil.getFileExtension(url) : undefined,
            }) as Asset),
        )
      )
    };

    const addAsset = rxMethod<File>(
      pipe(
        mergeMap(file => {
          const assetType = store.assetType();

          if (!assetType) return EMPTY;
          const id = DataUtil.generateId();

          patchState(store, addEntity({ id, status: 'pending' as CallStatus, url: undefined } as Asset));

          return assetService.uploadFile(file, assetType).pipe(
            tapResponse({
              next: asset => {
                patchState(
                  store,
                  updateEntity({
                    id,
                    changes: {
                      url: asset.url,
                      status: 'loaded',
                      extension: FileUtil.getFileExtension(asset.url),
                    }
                  })
                )
              },
              error: error => {
                patchState(store, updateEntity({ id, changes: { status: { error } } }))
              },
            }),
          );
        })
      ),
    );

    return {
      setAssetType,
      addAsset,
      initAssets,
      removeAsset,
      setAcceptType,
    };
  }),
  withLogger('asset-handler'),
);

